import type { SsoAuditEntry, SsoPolicyMode, SsoUpstream } from "@/lib/api";
import { UPSTREAM_LABELS } from "./sso.ts";

// One line for what a sign-in change did, from its before and after. No React
// and no api client at runtime, so `node --test` covers it. User-visible copy
// follows the product voice: "Boo", never "AI" / "agent" / "container".

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const text = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const listText = (items: string[], max = 2) =>
  items.length <= max ? items.join(", ") : `${items.slice(0, max).join(", ")} and ${items.length - max} more`;

const MODE_LABELS: Record<SsoPolicyMode, string> = {
  members: "your team",
  org_domain: "company domain",
  allowlist: "specific people",
  roster: "Slack workspace",
  open: "anyone who signs in",
};

/** "company domain (acme.com)", "specific people (3 emails)", "your team". */
function accessText(p: Record<string, unknown> | null): string {
  const mode = p?.mode as SsoPolicyMode | undefined;
  if (!mode || !(mode in MODE_LABELS)) return "unknown";
  const label = MODE_LABELS[mode];
  if (mode === "org_domain") {
    const domains = strings(p?.domains);
    return domains.length ? `${label} (${listText(domains)})` : label;
  }
  if (mode === "allowlist") return `${label} (${plural(strings(p?.emails).length, "email")})`;
  return label;
}

const providersText = (upstreams: string[]) =>
  upstreams.length ? upstreams.map((u) => UPSTREAM_LABELS[u as SsoUpstream] ?? u).join(", ") : "none";

function policyChange(before: Record<string, unknown> | null, after: Record<string, unknown> | null): string[] {
  const parts: string[] = [];
  const from = accessText(before);
  const to = accessText(after);
  if (from !== to) parts.push(`Access: ${from} → ${to}`);
  else if (before?.mode === "allowlist" && !sameList(strings(before?.emails), strings(after?.emails))) {
    parts.push(`Access: changed who's on the list (${plural(strings(after?.emails).length, "email")})`);
  } else if (before?.mode === "org_domain" && !sameList(strings(before?.domains), strings(after?.domains))) {
    parts.push(`Access: domains now ${listText(strings(after?.domains))}`);
  }
  const fromUp = strings(before?.upstreams);
  const toUp = strings(after?.upstreams);
  if (!sameList(fromUp, toUp)) {
    // An empty list means every provider the Boo has on.
    const label = (u: string[]) => (u.length ? providersText(u) : "all of this Boo's");
    parts.push(`Sign in with: ${label(fromUp)} → ${label(toUp)}`);
  }
  return parts;
}

function settingsChange(before: Record<string, unknown> | null, after: Record<string, unknown> | null): string[] {
  const parts: string[] = [];
  const fromUp = strings(before?.enabledUpstreams);
  const toUp = strings(after?.enabledUpstreams);
  if (!sameList(fromUp, toUp)) parts.push(`Sign in with: ${providersText(fromUp)} → ${providersText(toUp)}`);
  const fromDomains = strings(before?.extraDomains);
  const toDomains = strings(after?.extraDomains);
  if (!sameList(fromDomains, toDomains)) {
    parts.push(`Extra domains: ${fromDomains.length ? listText(fromDomains) : "none"} → ${toDomains.length ? listText(toDomains) : "none"}`);
  }
  return parts;
}

function appChange(before: Record<string, unknown> | null, after: Record<string, unknown> | null): string[] {
  const parts: string[] = [];
  const fromName = text(before?.name);
  const toName = text(after?.name);
  if (fromName !== toName) {
    parts.push(toName ? (fromName ? `Renamed “${fromName}” → “${toName}”` : `Named “${toName}”`) : "Name cleared");
  }
  const fromDescription = text(before?.description);
  const toDescription = text(after?.description);
  if (fromDescription !== toDescription) parts.push(toDescription ? "Description updated" : "Description cleared");
  return parts;
}

/** A short human summary of one change, e.g. "Access: company domain (acme.com) → specific people (3 emails)". */
export function ssoChangeSummary(entry: Pick<SsoAuditEntry, "event" | "before" | "after"> & Partial<Pick<SsoAuditEntry, "appId" | "appName">>): string {
  const { before, after } = entry;
  switch (entry.event) {
    case "policy_updated":
      return policyChange(before, after).join("; ") || "Access saved";
    case "policy_reset":
      return `Access back to the default: ${accessText(after)}`;
    case "settings_updated":
      return settingsChange(before, after).join("; ") || "Sign-in options saved";
    case "settings_reset": {
      const parts = settingsChange(before, after);
      return `Back to the suggested sign-in options${parts.length ? ` (${parts.join("; ")})` : ""}`;
    }
    case "app_updated":
      return appChange(before, after).join("; ") || "Sign-in page text saved";
    case "client_registered":
      return `Sign-in set up for ${entry.appName || entry.appId || "a new app"}`;
    case "ownership_taken":
      return "Now belongs to this Boo";
    default:
      return "Sign-in changed";
  }
}

/** Which app the change was to, or the Boo's own settings. */
export function ssoChangeTarget(entry: Pick<SsoAuditEntry, "appId" | "appName">): string {
  return entry.appId ? entry.appName || entry.appId : "Boo settings";
}

/** Who made it: "Boo" for the Boo itself, otherwise the person. */
export function ssoChangeActor(entry: Pick<SsoAuditEntry, "actorType" | "actorName" | "actorEmail">): string {
  if (entry.actorType === "agent") return "Boo";
  return entry.actorName || entry.actorEmail || "Someone no longer in this workspace";
}
